import { useState, useMemo } from "react";
import { X } from "lucide-react";
import { cn } from "@/utils/cn";
import { Statuses } from "@/utils/statuses";
import { BULK_EDIT_ID, BULK_EDIT_LABEL, DC_COMMON_TEXT } from "@/utils/draftCalendarFields";
import { getBulkEditPermissions } from "../permissions/bulkEditRules";
import { ProfileLookup, type ProfileObj } from "./ProfileLookup";
import { PushToSummitConfirmDialog } from "./PushToSummitConfirmDialog";
import type { BulkUpdateDraftFieldsRequest } from "@/types/draftCalendar";
import { useDUDeclinedReasons, useDraftBusinesses, useDraftMeetingTypes, useAttendeeLevels } from "@/services/draftCalendar";

const TIERS = [Statuses.Core, Statuses.Secondary];
const SIGN_OFF_STATUSES = ["Pending", "Signed Off", "Rejected"];

const openSansBodyTextCls = "font-['Open_Sans'] text-[14px] leading-5 font-normal text-[#63666A]";
const openSansSectionTitleCls = "font-['Open_Sans'] text-[16px] leading-6 font-semibold text-black";

// Input/select classes - borderless transparent, same as AddMeetingForm
const iCls = "w-full border-0 bg-transparent px-1.5 py-1.5 text-sm focus:outline-none focus:ring-0";
const sCls = "w-full border-0 bg-transparent px-1.5 py-1 text-sm focus:outline-none focus:ring-0";
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface BulkEditFlyoutProps {
  selectedCount: number;
  selectedIds: number[];
  userId: number;
  isBusinessApprover?: boolean;
  isBusinessReviewer?: boolean;
  onSave: (request: BulkUpdateDraftFieldsRequest) => void | Promise<void>;
  onClose: () => void;
  submitting?: boolean;
}

interface FormState {
  meetingName: string;
  draftEventState: string;
  signOffStatus: string;
  status: string;
  showInGRC: string;
  startTime: string;
  endTime: string;
  allInAttendees: string;
  programOwner: string;
  programOwnerEmail: string;
  programContact: string;
  programContactEmail: string;
  businessAdvisor: string;
  businessAdvisorEmail: string;
  programManager: string;
  programManagerEmail: string;
  business: string;
  meetingCategory: string;
  meetingType: string;
  businessLine: string;
  attendeeLevel: string;
  duDeclineReason: string;
  duDeclineComments: string;
}

const EMPTY_FORM: FormState = {
  meetingName: "", draftEventState: "", signOffStatus: "", status: "", showInGRC: "",
  startTime: "", endTime: "", allInAttendees: "",
  programOwner: "", programOwnerEmail: "",
  programContact: "", programContactEmail: "",
  businessAdvisor: "", businessAdvisorEmail: "",
  programManager: "", programManagerEmail: "",
  business: "", meetingCategory: "", meetingType: "", businessLine: "", attendeeLevel: "",
  duDeclineReason: "", duDeclineComments: "",
};

function sanitizeText(value: string): string {
  return value.replace(/[\u0000-\u001F\u007F]/g, "").trim();
}

function sanitizePersonName(value: string): string {
  const cleaned = sanitizeText(value)
    .replace(/[<>"'`]/g, "")
    .replace(/\s+/g, " ");
  return cleaned.slice(0, 120);
}

function sanitizeEmail(value: string): string {
  const normalized = sanitizeText(value).toLowerCase();
  if (!EMAIL_PATTERN.test(normalized)) return "";
  return normalized.slice(0, 254);
}

export function BulkEditFlyout({ selectedCount, selectedIds, userId, isBusinessApprover = false, isBusinessReviewer = false, onSave, onClose, submitting = false }: BulkEditFlyoutProps) {
  const [form, setForm] = useState<FormState>({ ...EMPTY_FORM });
  const [errors, setErrors] = useState<Partial<Record<'endTime' | 'allInAttendees' | 'businessLine' | 'meetingCategory', string>>>({});
  const [pendingPushRequest, setPendingPushRequest] = useState<BulkUpdateDraftFieldsRequest | null>(null);
  const permissions = getBulkEditPermissions({ isBusinessApprover, isBusinessReviewer });
  const { data: duDeclinedReasons = [] } = useDUDeclinedReasons();
  const { data: businesses = {} } = useDraftBusinesses();
  const { data: meetingTypeMap = {} } = useDraftMeetingTypes();
  const { data: attendeeLevels = [] } = useAttendeeLevels();

  // Derived options based on cascading selections
  const businessOptions = Object.keys(businesses);
  const businessLineOptions = form.business ? (businesses[form.business] ?? []) : [];

  const meetingTypeOptions = useMemo(() => Object.keys(meetingTypeMap), [meetingTypeMap]);

  const meetingCategoryOptions = useMemo(() => {
    if (!form.meetingType) return [];
    const cats = meetingTypeMap[form.meetingType];
    return Array.isArray(cats) ? cats : [];
  }, [meetingTypeMap, form.meetingType]);

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }


  function handleProfileSelect(nameKey: keyof FormState, emailKey: keyof FormState) {
    return (p: ProfileObj | null) => {
      const safeName = p ? sanitizePersonName(p.fullName ?? "") : "";
      const safeEmail = p ? sanitizeEmail(p.email ?? "") : "";
      setForm((prev) => ({ ...prev, [nameKey]: safeName, [emailKey]: safeEmail }));
    };
  }

  function handleStartTimeChange(value: string) {
    setForm((prev) => ({ ...prev, startTime: value, endTime: !value ? "" : prev.endTime }));
    if (!value) {
      setErrors((prev) => ({ ...prev, endTime: undefined }));
    } else {
      // Re-validate end time against new start time
      setErrors((prev) => {
        const end = form.endTime;
        if (end && end <= value) return { ...prev, endTime: 'Meeting end time must be after start time' };
        return { ...prev, endTime: undefined };
      });
    }
  }

  function handleEndTimeChange(value: string) {
    set('endTime', value);
    if (form.startTime && !value) {
      setErrors((prev) => ({ ...prev, endTime: 'Meeting end time is required when start time is set' }));
    } else if (form.startTime && value && value <= form.startTime) {
      setErrors((prev) => ({ ...prev, endTime: 'Meeting end time must be after start time' }));
    } else {
      setErrors((prev) => ({ ...prev, endTime: undefined }));
    }
  }

  function handleAllInAttendeesChange(value: string) {
    const normalized = sanitizeText(value).replace(/\s+/g, "");

    if (normalized === "") {
      set('allInAttendees', '');
      setErrors((prev) => ({ ...prev, allInAttendees: undefined }));
      return;
    }

    if (!/^[1-9][0-9]*$/.test(normalized)) {
      set('allInAttendees', normalized);
      setErrors((prev) => ({ ...prev, allInAttendees: 'Please enter valid number' }));
      return;
    }

    const parsed = Number(normalized);
    if (!Number.isSafeInteger(parsed)) {
      set('allInAttendees', normalized);
      setErrors((prev) => ({ ...prev, allInAttendees: 'Please enter a smaller number' }));
      return;
    }

    set('allInAttendees', normalized);
    setErrors((prev) => ({ ...prev, allInAttendees: undefined }));
  }

  async function handleSave() {
    const req: BulkUpdateDraftFieldsRequest = { draftIds: selectedIds, userId, role: "" };
    if (form.meetingName.trim()) req.meetingName = form.meetingName.trim();
    if (form.draftEventState) req.draftEventState = form.draftEventState;
    if (form.draftEventState === Statuses.DUDeclined && form.duDeclineReason) {
      req.duDeclineReason = form.duDeclineReason;
      if (form.duDeclineReason.toLowerCase() === "other" && form.duDeclineComments.trim()) {
        req.duDeclineComments = sanitizeText(form.duDeclineComments).slice(0, 500);
      }
    }
    if (form.signOffStatus) req.signOffStatus = form.signOffStatus;
    if (form.status) req.status = form.status;
    if (form.startTime) req.startTime = form.startTime;
    if (form.endTime) req.endTime = form.endTime;
    if (form.allInAttendees.trim()) {
      const attendees = Number(form.allInAttendees);
      if (!Number.isSafeInteger(attendees) || attendees <= 0) {
        setErrors((prev) => ({ ...prev, allInAttendees: 'Please enter valid number' }));
        return;
      }
      req.allInAttendees = attendees;
    }
    const programOwner = sanitizePersonName(form.programOwner);
    const programOwnerEmail = sanitizeEmail(form.programOwnerEmail);
    const programContact = sanitizePersonName(form.programContact);
    const programContactEmail = sanitizeEmail(form.programContactEmail);
    const businessAdvisor = sanitizePersonName(form.businessAdvisor);
    const businessAdvisorEmail = sanitizeEmail(form.businessAdvisorEmail);
    const programManager = sanitizePersonName(form.programManager);
    const programManagerEmail = sanitizeEmail(form.programManagerEmail);

    if (programOwner && programOwnerEmail) { req.programOwner = programOwner; req.programOwnerEmail = programOwnerEmail; }
    if (programContact && programContactEmail) { req.programContact = programContact; req.programContactEmail = programContactEmail; }
    if (businessAdvisor && businessAdvisorEmail) { req.businessAdvisor = businessAdvisor; req.businessAdvisorEmail = businessAdvisorEmail; }
    if (programManager && programManagerEmail) { req.programManager = programManager; req.programManagerEmail = programManagerEmail; }
    if (form.business) req.business = form.business;
    if (form.meetingType) req.meetingType = form.meetingType;
    if (form.meetingCategory) req.meetingCategory = form.meetingCategory;
    if (form.businessLine) req.businessLine = form.businessLine;
    if (form.attendeeLevel) req.attendeeLevel = form.attendeeLevel;
    if (form.showInGRC === "true" || form.showInGRC === "false") req.showInGRC = form.showInGRC === "true";
    const hasChanges = Object.keys(req).some((k) => k !== "draftIds" && k !== "userId");
    if (!hasChanges) return;

    // Cascading field validations
    const newErrors: typeof errors = { ...errors };
    if (form.business && !form.businessLine) {
      newErrors.businessLine = 'Business Line is required when Business is selected';
    } else {
      newErrors.businessLine = undefined;
    }
    if (form.meetingType && !form.meetingCategory) {
      newErrors.meetingCategory = 'Meeting Category is required when Meeting Type is selected';
    } else {
      newErrors.meetingCategory = undefined;
    }
    setErrors(newErrors);
    if (newErrors.endTime || newErrors.allInAttendees || newErrors.businessLine || newErrors.meetingCategory) return;

    if (form.draftEventState === Statuses.Resolved) {
      setPendingPushRequest(req);
      return;
    }

    await onSave(req);
  }

  const isDirty = Object.entries(form).some(([, v]) => v !== "");

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/30" onClick={onClose} aria-hidden="true" />
      <div role="dialog" aria-modal="true" aria-labelledby={BULK_EDIT_ID.DIALOG_TITLE}
        className="fixed inset-y-0 right-0 z-50 ml-auto flex h-full w-full max-w-3xl flex-col border-l border-slate-300 bg-white shadow-xl">

        {/* Header */}
        <div className="flex items-center justify-between bg-black px-4 py-3">
          <h2 id={BULK_EDIT_ID.DIALOG_TITLE} className="text-base font-semibold text-white">
            {BULK_EDIT_LABEL.DIALOG_TITLE_PREFIX} {selectedCount} Meeting{selectedCount > 1 ? "s" : ""}
          </h2>
          <button type="button" onClick={onClose} aria-label={BULK_EDIT_LABEL.CLOSE} className="text-white hover:text-slate-300">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form body - same styling as AddMeetingForm */}
        <div className="flex-1 overflow-y-auto px-4 py-4">
          <p className={cn("mb-3", openSansBodyTextCls)}>{DC_COMMON_TEXT.BULK_EDIT_HELP_TEXT}</p>

          {/* Meeting Name */}
          <Row><Col12><Field label={BULK_EDIT_LABEL.MEETING_NAME}><input type="text" value={form.meetingName} onChange={(e) => set("meetingName", e.target.value)} placeholder="--" className={iCls} /></Field></Col12></Row>
         
         {/* Times Header */}
          <div className="my-3">
            <span className={cn("align-middle", openSansSectionTitleCls)}>{BULK_EDIT_LABEL.DATES_TIMES_TITLE}</span>
            <span className={cn("align-middle block", openSansBodyTextCls)}>{BULK_EDIT_LABEL.DATES_TIMES_HELP_TEXT}</span>
          </div>
          
          {/* Meeting Start Time - Meeting End Time  */}
          <Row>
            <Field label={BULK_EDIT_LABEL.MEETING_START_TIME}><input id={BULK_EDIT_ID.START_TIME} type="time" value={form.startTime} onChange={(e) => handleStartTimeChange(e.target.value)} className={iCls} /></Field>
            <Field label={BULK_EDIT_LABEL.MEETING_END_TIME} error={errors.endTime}><input id={BULK_EDIT_ID.END_TIME} type="time" value={form.endTime} onChange={(e) => handleEndTimeChange(e.target.value)} min={form.startTime || undefined} className={cn(iCls, errors.endTime && "text-red-600")} /></Field>
          </Row>
         
          <hr className="my-[10px] border-0 border-t border-slate-300" />
          {/* All In Attendees */}
          <Row><Col12><Field label={BULK_EDIT_LABEL.ALL_IN_ATTENDEES} error={errors.allInAttendees}><input type="text" inputMode="numeric" pattern="[1-9][0-9]*" value={form.allInAttendees} onChange={(e) => handleAllInAttendeesChange(e.target.value)} placeholder="--" className={cn(iCls, errors.allInAttendees && "text-red-600")} /></Field></Col12></Row>

          {/* CLO or Sponsoring PPMD - Program Contact —*/}
          <Row>
            <Field label={BULK_EDIT_LABEL.PROGRAM_OWNER}><ProfileLookup id={BULK_EDIT_ID.PROGRAM_OWNER} value={form.programOwner} disabled={false} onSelect={handleProfileSelect("programOwner", "programOwnerEmail")} /></Field>
            <Field label={BULK_EDIT_LABEL.PROGRAM_CONTACT}><ProfileLookup id={BULK_EDIT_ID.PROGRAM_CONTACT} value={form.programContact} disabled={false} onSelect={handleProfileSelect("programContact", "programContactEmail")} /></Field>
          </Row>
          

          {/* Business Advisor - Program Manager — */}
          <Row>
            <Field label={BULK_EDIT_LABEL.BUSINESS_ADVISOR}><ProfileLookup id={BULK_EDIT_ID.BUSINESS_ADVISOR} value={form.businessAdvisor} disabled={false} onSelect={handleProfileSelect("businessAdvisor", "businessAdvisorEmail")} /></Field>
            <Field label={BULK_EDIT_LABEL.PROGRAM_MANAGER}><ProfileLookup id={BULK_EDIT_ID.PROGRAM_MANAGER} value={form.programManager} disabled={false} onSelect={handleProfileSelect("programManager", "programManagerEmail")} /></Field>
          </Row>
          

          {/* Draft Meeting Status - Sign Off Status — HIDDEN */}
          <Row>
            <Field label={BULK_EDIT_LABEL.DRAFT_MEETING_STATUS}><select value={form.draftEventState} onChange={(e) => set("draftEventState", e.target.value)} className={sCls}><option value="">--</option>{permissions.draftMeetingStatusOptions.map((s) => <option key={s} value={s}>{s}</option>)}</select></Field>
            {/* <Field label={BULK_EDIT_LABEL.SIGN_OFF_STATUS}><select value={form.signOffStatus} onChange={(e) => set("signOffStatus", e.target.value)} className={sCls}><option value="">--</option>{SIGN_OFF_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}</select></Field> */}
          </Row>

          {/* DU Decline Reason + Comments — shown when Draft Meeting Status is DU Declined */}
          {form.draftEventState === Statuses.DUDeclined && (
            <>
              <Row>
                <Field label="Reason for DU Decline">
                  <select value={form.duDeclineReason} onChange={(e) => set("duDeclineReason", e.target.value)} className={sCls}>
                    <option value="">--</option>
                    {duDeclinedReasons.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </Field>
              </Row>
              {form.duDeclineReason.toLowerCase() === "other" && (
                <Row><Col12><Field label="DU Decline Comments"><input type="text" value={form.duDeclineComments} onChange={(e) => set("duDeclineComments", e.target.value)} placeholder="--" className={iCls} /></Field></Col12></Row>
              )}
            </>
          )}
          
          <hr className="my-[10px] border-0 border-t border-slate-300" />

          {/* Business - Business Line */}
          <Row>
            <Field label={BULK_EDIT_LABEL.BUSINESS}><select value={form.business} onChange={(e) => { set("business", e.target.value); set("businessLine", ""); setErrors((prev) => ({ ...prev, businessLine: undefined })); }} className={sCls}><option value="">--</option>{businessOptions.map((b) => <option key={b} value={b}>{b}</option>)}</select></Field>
            <Field label={BULK_EDIT_LABEL.BUSINESS_LINE} error={errors.businessLine}><select value={form.businessLine} onChange={(e) => { set("businessLine", e.target.value); setErrors((prev) => ({ ...prev, businessLine: undefined })); }} disabled={!form.business} className={cn(sCls, errors.businessLine && "text-red-600")}><option value="">--</option>{businessLineOptions.map((bl) => <option key={bl} value={bl}>{bl}</option>)}</select></Field>
          </Row>

          {/* Meeting Type - Meeting Category */}
          <Row>
            <Field label={BULK_EDIT_LABEL.MEETING_TYPE}><select value={form.meetingType} onChange={(e) => { set("meetingType", e.target.value); set("meetingCategory", ""); setErrors((prev) => ({ ...prev, meetingCategory: undefined })); }} className={sCls}><option value="">--</option>{meetingTypeOptions.map((mt) => <option key={mt} value={mt}>{mt}</option>)}</select></Field>
            <Field label={BULK_EDIT_LABEL.MEETING_CATEGORY} error={errors.meetingCategory}><select value={form.meetingCategory} onChange={(e) => { set("meetingCategory", e.target.value); setErrors((prev) => ({ ...prev, meetingCategory: undefined })); }} disabled={!form.meetingType} className={cn(sCls, errors.meetingCategory && "text-red-600")}><option value="">--</option>{meetingCategoryOptions.map((mc) => <option key={mc} value={mc}>{mc}</option>)}</select></Field>
          </Row>

          {/* Attendee Level */}
          <Row>
            <Field label={BULK_EDIT_LABEL.ATTENDEE_LEVEL}><select value={form.attendeeLevel} onChange={(e) => set("attendeeLevel", e.target.value)} className={sCls}><option value="">--</option>{attendeeLevels.map((al) => <option key={al} value={al}>{al}</option>)}</select></Field>
          </Row>

          {/* Admin section */}
          {permissions.canViewAdminSection && (
            <>
              <div className="my-3">
                <span className={cn("align-middle", openSansSectionTitleCls)}>{BULK_EDIT_LABEL.ADMIN}</span>
              </div>
              <Row>
                <Field label={BULK_EDIT_LABEL.TIER}><select value={form.status} onChange={(e) => set("status", e.target.value)} className={sCls}><option value="">--</option>{TIERS.map((t) => <option key={t} value={t}>{t}</option>)}</select></Field>
                <Field label={BULK_EDIT_LABEL.SHOW_IN_GRC}><select value={form.showInGRC} onChange={(e) => set("showInGRC", e.target.value)} className={sCls}><option value="">--</option><option value="true">Yes</option><option value="false">No</option></select></Field>
              </Row>
            </>
          )}

        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 border-t border-slate-200 px-4 py-3">
          <button type="button" onClick={onClose} className="rounded-none border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">Cancel</button>
          <button type="button" onClick={handleSave} disabled={submitting || !isDirty || !!errors.endTime || !!errors.allInAttendees || !!errors.businessLine || !!errors.meetingCategory}
            className={cn("rounded-none border border-slate-900 bg-slate-900 px-4 py-2 text-sm text-white", "hover:bg-slate-800 disabled:opacity-60 disabled:cursor-not-allowed")}>
            {submitting ? "Saving\u2026" : "Save"}
          </button>
        </div>
      </div>

      <PushToSummitConfirmDialog
        open={pendingPushRequest !== null}
        message={DC_COMMON_TEXT.PUSH_TO_SUMMIT_CONFIRM}
        submitting={submitting}
        onCancel={() => setPendingPushRequest(null)}
        onConfirm={() => {
          if (!pendingPushRequest) return;
          void onSave(pendingPushRequest);
          setPendingPushRequest(null);
        }}
      />
    </>
  );
}

// ── Layout helpers (same as AddMeetingForm) ──────────────────────────────────

function Row({ children }: { children: React.ReactNode }) {
  return <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>{children}</div>;
}
function Col12({ children }: { children: React.ReactNode }) {
  return <div style={{ gridColumn: "1 / -1" }}>{children}</div>;
}
function Field({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <div>
      <div className={cn("border border-[#D0D0CE] px-1.5 pb-1.5", error && "border-red-400")}>
        <label className="mt-0.5 ml-1.5 block text-[11px] font-semibold text-[#53565A]">{label}</label>
        {children}
      </div>
      {error && <p role="alert" className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}
